import { z } from "zod";
import type { ApiSuccess } from "@core/types";
import { logger } from "@core/utils/logger";

export async function handleResponse<T>(
  promise: Promise<ApiSuccess<T>>,
): Promise<T> {
  const result = await promise;
  return result.data as T;
}

export async function handleMappedResponse<TRaw, TOut>(
  promise: Promise<ApiSuccess<TRaw>>,
  schema: z.ZodType<TOut>,
  mapper: (raw: TOut) => TOut,
  context?: string,
): Promise<TOut> {
  const result = await promise;
  const parsed = schema.safeParse(result.data);
  if (!parsed.success) {
    logger.zod(parsed.error.issues, context);
    throw {
      success: false,
      message: "Response validation failed",
      error: {
        code: "PARSE_ERROR",
        message: "API response did not match expected shape",
        details: parsed.error.issues,
      },
      data: null,
    };
  }
  return mapper(parsed.data);
}

export async function handlePaginatedResponse<TOut>(
  promise: Promise<ApiSuccess<unknown>>,
  itemSchema: z.ZodType<TOut>,
  itemMapper: (raw: TOut) => TOut,
  context?: string,
): Promise<{
  items: TOut[];
  meta: unknown;
  permissions?: unknown;
}> {
  const result = await promise;

  const raw = result.data as {
    items?: unknown[];
    meta?: unknown;
    permissions?: unknown;
  };

  const items: TOut[] = [];
  const rawItems = raw.items ?? [];

  for (let i = 0; i < rawItems.length; i++) {
    const parsed = itemSchema.safeParse(rawItems[i]);
    if (parsed.success) {
      items.push(itemMapper(parsed.data));
    } else {
      logger.zodItem(i, parsed.error.issues, rawItems[i]);
      if (context) logger.warn(`↳ context: ${context}`);
    }
  }

  return {
    items,
    meta: raw.meta,
    ...(raw.permissions !== undefined && {
      permissions: raw.permissions,
    }),
  };
}
